/* eslint-disable import/prefer-default-export */
import styled, { css } from "styled-components";
import visuallyHidden from "theme/visually-hidden";

export const FilledLabelWrapper = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize(1)};
  color: ${({ theme }) => theme.darkColors.secondary};
  line-height: 1.125rem;
  margin-bottom: 0;
  font-weight: 400;

  ${({ showRequiredAsterisk }) =>
    showRequiredAsterisk &&
    css`
      width: 12.5rem;
      display: flex;
    `}
`;

export const Input = css`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme?.color?.dark};
  font-size: ${({ theme }) => theme.fontSize(2)};
  font-weight: 400;
  height: 2.625rem;
  padding: 0;
  width: 100%;

  ${({ bigFont, theme }) =>
    bigFont &&
    css`
      font-weight: 600;
      font-size: ${theme.fontSize(4)};
    `}

  :focus {
    outline: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.themeColors.secondary};
    font-weight: 400;
    opacity: 1; /* Firefox */
  }

  :read-only {
    color: ${({ theme }) => theme.themeColors.darkGreen2};
    pointer-events: none;
    opacity: 1;
  }

  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus {
    background-clip: border-box;
    background-color: ${({ styleType, theme }) =>
    (styleType === "filled" && theme.themeColors.subtleLight) || "transparent"};
    box-shadow: ${({ styleType, theme }) => {
        if (styleType === "filled") return `0 0 0 100vw ${theme.themeColors.subtleLight} inset`;
        return `0 0 0 100vw white inset`;
      }};
    -webkit-text-fill-color: ${({ theme }) => theme?.color?.dark};
    -webkit-box-shadow: ${({ styleType, theme }) => {
      if (styleType === "filled") return `0 0 0 100vw ${theme.themeColors.subtleLight} inset`;
      return `0 0 0 100vw white inset`;
    }};
    height: 100%;
  }
`;

export const InputText = styled.input`
  ${Input}
`;

export const InputTextArea = styled.textarea`
  ${Input}
  border: 0 none;
  margin-top: 0.75rem;
  min-height: 5.25rem;
`;

export const InputTextWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  border: 0.0625rem solid;
  border-color: ${({ styleType, theme, success, error }) => {
    if (success && styleType === "filled") return theme.themeColors.success;
    if (error && styleType === "filled") return theme.themeColors.danger;
    return "transparent";
  }};
  border-bottom-color: ${({ error, styleType, success, theme }) => {
    if (success) return theme.themeColors.success;
    if (error) return theme.themeColors.orange2;
    if (styleType === "material") return theme.themeColors.secondary;
    return "transparent";
  }};
  background-color: ${({ styleType, theme }) =>
    (styleType === "filled" && theme.themeColors.subtleLight) || "transparent"};
  border-radius: ${({ styleType }) =>
    (styleType === "filled" && "0.5rem") || "0"};
  height: 2.625rem;
  justify-content: space-between;
  margin: 0;
  transition: border-color 0.3s;
  padding: 0 ${({ styleType }) => (styleType === "filled" && "1rem") || 0};
  width: ${({ width }) => width || "100%"};

  :hover {
    border-bottom-color: ${({ error, styleType, success, theme }) => {
      if (success) return theme.themeColors.success;
      if (error) return theme.themeColors.danger;
      if (styleType === "material") return theme.themeColors.secondary;
      return "transparent";
    }};

    border-color: ${({ styleType, theme }) => {
      return styleType === "filled" && theme.themeColors.secondary;
    }};
  }

  :focus-within {
    border-bottom-color: ${({ theme, readOnly }) =>
      (readOnly && theme.themeColors.secondary) || theme.themeColors.primary};

    border-color: ${({ styleType, theme }) => {
      return styleType === "filled" && theme.themeColors.primary;
    }};
  }
`;

export const InputTextAreaWrapper = styled(InputTextWrapper)`
  border-bottom-color: ${({ error, styleType, success, touched, theme }) => {
    if (success && touched) return theme.themeColors.success;
    if (error && touched) return theme.themeColors.danger;
    if (styleType === "material") return theme.themeColors.secondary;
    return "transparent";
  }};
  height: auto;
  margin: 0 0 0.25rem;
  padding: 0 0 0.25rem;
`;

export const LimitContainer = styled.div`
  font-size: ${({ theme }) => theme.fontSize(0)};
  color: #8a8685;
  display: flex;
  justify-content: flex-end;
`;

export const MaterialLabelWrapper = styled.label`
  position: absolute;
  display: block;
  font-size: ${({ theme }) => theme.fontSize(2)};
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  line-height: 1.125rem;
  pointer-events: none;
  top: 0.75rem;
  transition: all 0.2s;
  font-weight: 400;

  ${({ hideLabelForPlaceholder }) =>
    hideLabelForPlaceholder &&
    css`
      ${visuallyHidden};
    `}

  ${({ showRequiredAsterisk, theme }) =>
    showRequiredAsterisk &&
    css`
      :after {
        content: "*";
        color: ${theme.themeColors.primary};
        font-size: 1rem;
        font-weight: 400;
        line-height: 0.3125rem;
        position: absolute;
        right: -0.625rem;
        top: 50%;
      }
    `}

  ${({ hasLeadingIcon }) =>
    hasLeadingIcon &&
    css`
      transform: translateX(1.75rem);
    `}

  ${({ isActive }) =>
    isActive &&
    css`
      font-size: ${({ theme }) => theme.fontSize(1)};
      transform: translate(0, -1.5625rem);
    `}
`;

export const MessageWrapper = styled.div`
  display: block;
  font-size: ${({ theme }) => theme.fontSize(0)};
  color: ${({ success, theme, touched }) => {
    if (success || (success && touched)) {
      return theme.themeColors.success;
    }
    return theme.themeColors.dark;
  }};
`;

export const RequiredWrapper = styled.div`
  color: ${({ theme }) => theme.themeColors.primary};
  font-weight: 400;
  font-size: 1rem;
  line-height: 0.3125rem;
  margin: 0.5rem 0 0 0.1875rem;
`;

export const OuterInputWrapper = styled.div`
  margin: 0;
  position: relative;
  width: 100%;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.8;
    `}
`;
